import { withStyles } from "@material-ui/core";
import React from "react";
import type { ReactNode } from "react";
import { connect } from "react-redux";
import {
  APIProvider,
  Map,
  useMapsLibrary,
} from "@vis.gl/react-google-maps/dist/index.umd.js";
import * as actions from "redux/actions";
import guid from "uuid/v1";
import mapaConstants from "constants/mapaFarolAlturaPasto";
import type { MapCameraChangedEvent } from "@vis.gl/react-google-maps";
import {
  getPolygonPaths,
  type PolygonLike,
} from "./MapaGoogleCustomizado.helpers";
import DrawingManager from "./MapaGoogleCustomizadoDrawingManager";
import { useDrawingKeyboardShortcuts } from "./useDrawingKeyboardShortcuts";
import { useMapCameraSync } from "./useMapCameraSync";

type GoogleMapZoomChangedHandler = (this: {
  zoom?: number;
  getZoom: () => number | undefined;
}) => void;

interface MapaGoogleClasses {
  content: string;
}

interface MapaGoogleReduxStateProps {
  Desenhando: boolean;
}

interface MapaGoogleReduxDispatchProps {
  coordenadasNovoPasto: typeof actions.salvarCoordNovoPasto;
  desabilitarDesenhar: typeof actions.desabilitarDesenhar;
  habilitarDesenhar: typeof actions.habilitarDesenhar;
}

interface MapaGoogleOwnProps {
  coordenadasCentro: google.maps.LatLngLiteral;
  onZoomChanged?: GoogleMapZoomChangedHandler;
  zoomPadrao?: number;
  onMapMounted?: unknown;
  zoom?: number;
  mapTypeId?: string;
  mapTypeControl?: boolean;
  scrollwheel?: boolean;
  zoomControl?: boolean;
  panControl?: boolean;
  draggable?: boolean;
  scaleControl?: boolean;
  children?: ReactNode;
  classes?: MapaGoogleClasses;
}

type MapaGoogleContentProps = MapaGoogleOwnProps &
  MapaGoogleReduxStateProps &
  MapaGoogleReduxDispatchProps;

const noopThunkCreator = (() => () =>
  undefined) as typeof actions.desabilitarDesenhar;
const noopSalvarCoord = ((
  ..._args: Parameters<typeof actions.salvarCoordNovoPasto>
) =>
  () =>
    undefined) as typeof actions.salvarCoordNovoPasto;

export function MapaGoogleCustomizado(props: MapaGoogleContentProps) {
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? "";
  const googleMapsVersion =
    process.env.REACT_APP_GOOGLE_MAPS_VERSION_API_KEY ?? "weekly";

  return (
    <APIProvider
      apiKey={googleMapsApiKey}
      version={googleMapsVersion}
      libraries={mapaConstants.google_maps.libraries}
    >
      <MapaGoogleContent {...props} />
    </APIProvider>
  );
}

export function MapaGoogleContent({
  coordenadasCentro,
  onZoomChanged,
  zoomPadrao,
  onMapMounted,
  mapTypeId,
  scrollwheel,
  zoomControl,
  panControl,
  draggable,
  scaleControl,
  classes,
  children,
  desabilitarDesenhar = noopThunkCreator,
  habilitarDesenhar = noopThunkCreator,
  coordenadasNovoPasto = noopSalvarCoord,
  Desenhando = false,
  mapTypeControl,
  ...props
}: MapaGoogleContentProps) {
  void onMapMounted;
  const disableDefaultUI = true;
  const geometryLibrary = useMapsLibrary("geometry");

  useDrawingKeyboardShortcuts({
    disableDrawing: desabilitarDesenhar,
    enableDrawing: habilitarDesenhar,
  });

  useMapCameraSync({ center: coordenadasCentro, zoom: zoomPadrao });

  const handleZoomChanged = (event: MapCameraChangedEvent) => {
    if (!onZoomChanged) {
      return;
    }

    const zoomValue = event.detail.zoom ?? event.map.getZoom();

    onZoomChanged.call({
      zoom: zoomValue,
      getZoom: () => zoomValue,
    });
  };

  const onPolygonComplete = (poly: PolygonLike) => {
    const paths = getPolygonPaths(poly);

    if (!paths.length) {
      return;
    }

    const area = geometryLibrary
      ? (geometryLibrary.spherical.computeArea(paths) / 10000).toFixed(1)
      : 0;

    if (typeof poly.getPath === "function") {
      const polygonId = poly.Id || guid();
      poly.Id = polygonId;
      coordenadasNovoPasto(paths, poly, area);
      return;
    }

    const polygonId = poly.Id || poly.id || guid();
    coordenadasNovoPasto(
      paths,
      { Id: polygonId, setMap: () => undefined },
      area,
    );
  };

  const renderDesenhar = () => {
    return Desenhando ? (
      <DrawingManager
        options={{
          drawingControl: false,
          drawingMode: mapaConstants.google_maps.drawing_mode.polyline,
          polygonOptions: {
            strokeWeight: 1,
            strokeColor: "#00FF00",
            fillColor: "#00FF00",
            fillOpacity: 0.15,
            clickable: true,
            editable: true,
            zIndex: 1,
          },
        }}
        onPolygonComplete={onPolygonComplete}
      />
    ) : null;
  };

  const opcoesMapa = {
    mapId: process.env.REACT_APP_GOOGLE_MAPS_MAP_ID,
    mapTypeId,
    disableDefaultUI,
    mapTypeControl,
    scrollwheel,
    zoomControl,
    panControl,
    draggable,
    scaleControl,
    fullscreenControl: true,
  };

  return (
    <Map
      className={classes?.content}
      defaultZoom={zoomPadrao}
      defaultCenter={coordenadasCentro}
      onZoomChanged={handleZoomChanged}
      {...opcoesMapa}
    >
      {children}
      {renderDesenhar()}
    </Map>
  );
}

interface MapaGoogleReduxStoreState {
  pasto: {
    CoordenadasNovoPasto: unknown[];
    Desenhando: boolean;
  };
}

const mapDispatchToProps = {
  coordenadasNovoPasto: actions.salvarCoordNovoPasto,
  desabilitarDesenhar: actions.desabilitarDesenhar,
  habilitarDesenhar: actions.habilitarDesenhar,
};

function mapStateToProps(
  state: MapaGoogleReduxStoreState,
): MapaGoogleReduxStateProps {
  return {
    Desenhando: state.pasto.Desenhando,
  };
}

const MapaGoogleCustomizadoConnected = connect(
  mapStateToProps,
  mapDispatchToProps,
)(MapaGoogleCustomizado as React.ComponentType<MapaGoogleContentProps>);

const MapaGoogleCustomizadoWithStyles = withStyles((theme) => {
  const themeProps = (
    theme as unknown as {
      props?: {
        tamanhoAppBarSm?: number;
        tamanhoAppBarMd?: number;
        tamanhoAppBarXs?: number;
      };
    }
  ).props;

  return {
    content: {
      width: "100%",
      height: `calc(100vh - ${themeProps?.tamanhoAppBarSm ?? 0}px)`,
      "@media (min-width: 769px)": {
        height: `calc(100vh - ${themeProps?.tamanhoAppBarMd ?? 0}px)`,
      },
      "@media (min-width: 1128px)": {
        height: `calc(100vh - ${themeProps?.tamanhoAppBarXs ?? 0}px)`,
      },
    },
  };
})(
  MapaGoogleCustomizadoConnected as unknown as React.ComponentType<
    Record<string, unknown>
  >,
);

export default MapaGoogleCustomizadoWithStyles;
